/**
 * 사용자 정의 절대 규칙 사격 4단계 페이즈 상태 머신
 *
 * [순차 규칙]: 준비(SETUP) -> 당김(DRAW) -> 조준(ANCHOR) -> 발사(RELEASE) -> 준비(SETUP) 순서 보장
 * [공통 리셋]: 다른 단계(DRAW, ANCHOR 등)에서 준비 단계 조건(받침팔<=70 && 당김팔<=70)이 2초 이상 유지 시 준비로 초기화
 *
 * 1. 준비(SETUP):
 *    - 받침 팔 각도 <= 70° AND 당김 팔 각도 <= 70°
 *    - SETUP 상태에서는 2초 이상 유지되어도 초기화 없이 SETUP 유지
 * 2. 당김(DRAW) 전환:
 *    - 지탱 각도(bowArmAngle)가 160° 이상에 도달한 이력이 있는 상태에서
 *    - 받침 팔 각도 >= 70° AND 앵커 각도 >= 50° 만족 시 전환
 * 3. 조준(ANCHOR) 전환:
 *    - 받침 팔 각도 >= 70° AND 앵커 각도 <= 50° 상태 중,
 *    - 앵커 각도 변동 폭(최댓값 - 최솟값)이 4° 이하인 안정 상태를 1초(1000ms) 이상 유지 시 전환
 *    - 조준 전환 시점의 '당김 팔 각도'를 기준값(anchorRefDrawArmAngle)으로 캡처
 * 4. 발사(RELEASE) 전환:
 *    - 조준 단계에서 캡처된 당김 팔 각도보다 10° 이상 내려갈 시(angleDrop >= 10°) 전환
 *    - 발사 후 준비 단계 조건 진입 또는 1.5초 경과 시 준비(SETUP)로 순환
 */
export class ArcheryPhaseAnalyzer {
  constructor() {
    this.phase = 'SETUP'; // 'SETUP' | 'DRAW' | 'ANCHOR' | 'RELEASE'
    this.setupHoldStart = null;    // 다른 단계에서 준비 조건(<=70) 유지 시작 시각 (2초 타이머)
    this.bowArmReady = false;      // 준비 단계에서 지탱 각도 160° 이상 달성 여부 플래그
    this.anchorHoverSamples = [];  // 조준 전환 판정을 위한 앵커 각도 샘플 큐 [{ timestamp, angle }]
    this.anchorStartTime = null;   // 1초 후 조준 확정된 시각
    this.anchorDuration = 0;
    this.stabilityScore = 100;
    this.anchorRefDrawArmAngle = null; // 조준 확정 시 당김 팔 각도 기준값
    this.jitterSamples = [];
    this.lastWristPos = null;
    this.releaseTime = null;
  }

  analyze(landmarks, timestamp, angles) {
    if (!landmarks || landmarks.length < 25) {
      return this.getResult();
    }

    const isLeftHanded = angles?.dominantHand === 'left';
    const drawWrist = isLeftHanded ? landmarks[15] : landmarks[16];

    // 미세 떨림 추적용 속도 (조준 안정성 점수)
    let speed = 0;
    if (drawWrist) {
      if (this.lastWristPos) {
        speed = Math.hypot(drawWrist.x - this.lastWristPos.x, drawWrist.y - this.lastWristPos.y);
      }
      this.lastWristPos = { x: drawWrist.x, y: drawWrist.y };
    }

    const supportArmAngle = angles?.angleSupportArm ?? 0;
    const drawArmAngle = angles?.angleDrawArm ?? 0;
    const anchorElbowAngle = angles?.drawElbowAngle ?? 0;
    const bowArmAngle = angles?.bowArmAngle ?? 0;

    // -------------------------------------------------------------
    // [준비 조건 판정]: 받침 팔 각도와 당김 팔 각도가 모두 70도 이하
    // -------------------------------------------------------------
    const isSetupPose = supportArmAngle <= 70 && drawArmAngle <= 70;

    // -------------------------------------------------------------
    // [2초 유지 리셋 규칙]:
    // - 준비(SETUP) 상태일 때는 2초 이상 유지되어도 리셋 없이 계속 SETUP 유지
    // - 다른 단계(DRAW, ANCHOR 등)일 때 준비 조건이 2초 이상 유지되면 SETUP으로 초기화
    // -------------------------------------------------------------
    if (this.phase !== 'SETUP') {
      if (isSetupPose) {
        if (!this.setupHoldStart) {
          this.setupHoldStart = timestamp;
        } else if (timestamp - this.setupHoldStart >= 2000) {
          this.reset();
          return this.getResult();
        }
      } else {
        this.setupHoldStart = null;
      }
    } else {
      this.setupHoldStart = null;
    }

    // -------------------------------------------------------------
    // [단방향 4단계 순차 페이즈 전이] (스킵 불가: SETUP -> DRAW -> ANCHOR -> RELEASE -> SETUP)
    // -------------------------------------------------------------
    switch (this.phase) {
      case 'SETUP': {
        // [지탱 각도 선행 체크]: 지탱 각도가 160도 이상 도달 시 플래그 활성화
        if (bowArmAngle >= 160) {
          this.bowArmReady = true;
        }

        // [당김 전환 조건]: 지탱 각도 160도 이상 달성 이력 후, 받침 팔 각도 >= 70도 AND 앵커 각도 >= 50도
        if (this.bowArmReady && supportArmAngle >= 70 && anchorElbowAngle >= 50) {
          this.phase = 'DRAW';
          this.bowArmReady = false;
          this.anchorHoverSamples = [];
          this.setupHoldStart = null;
        }
        break;
      }

      case 'DRAW': {
        // [조준 전환 조건]: 받침 팔 각도 >= 70도 AND 앵커 각도 <= 50도 중에 각도 폭이 4도 이하로 1초(1000ms) 이상 유지 시
        const isAnchorCondition = supportArmAngle >= 70 && anchorElbowAngle <= 50;

        if (isAnchorCondition) {
          this.anchorHoverSamples.push({ timestamp, angle: anchorElbowAngle });

          // 큐 내의 앵커 각도 변동 폭(최댓값 - 최솟값)이 4도 이하가 될 때까지 오래된 샘플 제거
          while (this.anchorHoverSamples.length > 0) {
            let minAngle = this.anchorHoverSamples[0].angle;
            let maxAngle = this.anchorHoverSamples[0].angle;
            for (let i = 1; i < this.anchorHoverSamples.length; i++) {
              const a = this.anchorHoverSamples[i].angle;
              if (a < minAngle) minAngle = a;
              if (a > maxAngle) maxAngle = a;
            }
            if (maxAngle - minAngle <= 4) {
              break;
            }
            this.anchorHoverSamples.shift();
          }

          // 각도 폭 4도 이하 안정 구간이 1초(1000ms) 이상 유지되었는지 확인
          if (this.anchorHoverSamples.length > 0) {
            const holdDuration = timestamp - this.anchorHoverSamples[0].timestamp;
            if (holdDuration >= 1000) {
              this.phase = 'ANCHOR';
              this.anchorStartTime = timestamp;
              // 조준 전환 시점의 당김 팔 각도 체크 및 기준값으로 저장
              this.anchorRefDrawArmAngle = drawArmAngle;
              this.jitterSamples = [];
              this.anchorHoverSamples = [];
            }
          }
        } else {
          // 조건 벗어나면 앵커 샘플 큐 리셋
          this.anchorHoverSamples = [];
        }
        break;
      }

      case 'ANCHOR': {
        // 앵커링 유지 시간 실시간 누적
        const anchorElapsed = timestamp - this.anchorStartTime;
        this.anchorDuration = (anchorElapsed / 1000).toFixed(1);

        // 조준 안정성(미세 떨림) 누적
        if (drawWrist) {
          this.jitterSamples.push(speed);
          const avgJitter = this.jitterSamples.reduce((a, b) => a + b, 0) / this.jitterSamples.length;
          this.stabilityScore = Math.max(70, Math.min(100, Math.round(100 - avgJitter * 4000)));
        }

        // [발사 전환 조건]: 조준 전환 시 체크된 당김 팔 각도보다 10도 이상 내려갈 때
        if (this.anchorRefDrawArmAngle !== null) {
          const angleDrop = this.anchorRefDrawArmAngle - drawArmAngle;
          if (angleDrop >= 10) {
            this.phase = 'RELEASE';
            this.releaseTime = timestamp;
            this.setupHoldStart = null;
          }
        }
        break;
      }

      case 'RELEASE': {
        // [발사 이후 준비 단계로 전환]:
        // 1) 준비 단계 각도 조건에 진입하거나
        // 2) 발사 후 1.5초(1500ms) 경과 시 안전하게 준비 단계로 복귀
        if (isSetupPose || (this.releaseTime && timestamp - this.releaseTime > 1500)) {
          this.reset();
        }
        break;
      }

      default:
        this.reset();
        break;
    }

    return this.getResult();
  }

  getResult() {
    return {
      phase: this.phase,
      anchorDuration: Number(this.anchorDuration) || 0,
      stabilityScore: this.stabilityScore,
      anchorRefDrawArmAngle: this.anchorRefDrawArmAngle,
    };
  }

  reset() {
    this.phase = 'SETUP';
    this.setupHoldStart = null;
    this.bowArmReady = false;
    this.anchorHoverSamples = [];
    this.anchorStartTime = null;
    this.anchorDuration = 0;
    this.stabilityScore = 100;
    this.anchorRefDrawArmAngle = null;
    this.jitterSamples = [];
    this.lastWristPos = null;
    this.releaseTime = null;
  }
}

export const phaseAnalyzer = new ArcheryPhaseAnalyzer();



